import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchFleetOverview } from '../../api/equipment';
import { EquipmentHealthValue } from '../../lib/EquipmentHealthValue';
import {
  EQUIPMENT_TYPE_HINT_KEY,
  HEADER_FLEET_TYPES,
  SCHOOL_EQUIPMENT_SECTION_ID,
  schoolByRouterIp,
} from '../../lib/fleetHeaderStats';

const scrollToSection = (id) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

/**
 * Полоса оборудования школы: 4 плитки на всю ширину, название слева, цифры справа.
 */
const SchoolEquipmentTiles = ({ routerIp, onFire = false, className = '' }) => {
  const { t } = useTranslation();
  const [byType, setByType] = useState(null);

  useEffect(() => {
    if (!routerIp) return undefined;
    let cancelled = false;
    const load = async () => {
      try {
        const overview = await fetchFleetOverview();
        if (cancelled) return;
        setByType(schoolByRouterIp(overview, routerIp)?.byType || {});
      } catch {
        if (!cancelled) setByType({});
      }
    };
    load();
    const timer = setInterval(load, 60_000);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [routerIp]);

  const go = useCallback((type) => {
    scrollToSection(SCHOOL_EQUIPMENT_SECTION_ID[type]);
  }, []);

  const loaded = byType != null;

  return (
    <div className={`grid w-full grid-cols-2 grid-rows-2 gap-2 ${className}`.trim()}>
      {HEADER_FLEET_TYPES.map(({ type, icon }) => {
        const total = Number(byType?.[type]?.total) || 0;
        const empty = loaded && total === 0;
        const burning = onFire && type === 'fire' && !empty;
        const tileClass = empty
          ? 'bg-white/[0.03] opacity-60'
          : burning
            ? 'bg-critical/15 hover:bg-critical/20'
            : 'bg-white/[0.03] hover:bg-white/[0.06]';

        return (
          <button
            key={type}
            type="button"
            onClick={() => go(type)}
            title={t(EQUIPMENT_TYPE_HINT_KEY[type])}
            className={`flex h-full min-h-0 items-center gap-3 rounded-2xl px-3.5 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 ${tileClass}`}
          >
            <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/[0.04] text-brand-100">
              <span className="material-icons text-[16px]">{icon}</span>
            </span>
            <span className="min-w-0 flex-1 truncate text-sm text-muted-300">
              {t(`equipment.types.${type}`)}
            </span>
            {burning && (
              <span className="inline-flex shrink-0 items-center gap-1 text-[10px] text-critical">
                <span className="h-1.5 w-1.5 rounded-full bg-critical" aria-hidden />
                {t('fire.onFire')}
              </span>
            )}
            <span className="shrink-0">
              {!loaded ? (
                <span className="text-sm text-muted-500">—</span>
              ) : empty ? (
                <span className="text-sm text-muted-500">{t('equipment.noDevices')}</span>
              ) : (
                <EquipmentHealthValue
                  typeSummary={byType?.[type]}
                  type={type}
                  className="text-base"
                />
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default SchoolEquipmentTiles;
